import { Button, Card, Descriptions, Message, Modal, Popconfirm, Space, Table, Tag } from "@arco-design/web-react";
import { SOAPI, SOPOAPI, SOFulfillmentAPI, CustomerAPI, InvoiceAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Number, Currency } from "@/components/Numeric";
import { FileUtil } from "@/utils";
import { SOStatusDef, SOPOStatusDef, FulfillmentStatusDef, InvoiceStatusDef, SOStatus } from "@/enums/sales";
import { TermsDef } from "@/enums/common";
import { ItemType } from "@/enums/material";
import { EnumLabel } from "@/components/Enum";
import Address from "@/components/Address";
import _ from "lodash";
import qs from "qs";
import SendEmailButton from "@/components/SendEmailButton";
import Reference from "@/components/Reference";
import Decimal from "decimal.js";

const CreateInvoiceButton = ({ soInfo }) => {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [visible, setVisible] = useState(false);
    const [fulfillmentIds, setFulfillmentIds] = useState();

    const getFulfillments = async () => {
        const values = await SOAPI.noInvoiceFulfillments(soInfo.id);
        setData(values);
    }

    const createInvoice = async () => {
        let hasInventoryItem = false;
        for (const i in soInfo.items) {
            const item = await ItemAPI.get(soInfo.items[i].itemId);
            if (item.itemType === ItemType.Inventory) {
                hasInventoryItem = true;
                break;
            }
        }
        if (!hasInventoryItem) {
            navigate(`/workspace/sales/invoice/so/${soInfo.id}`);
            return;
        }

        const noInvoiceFulfillments = await SOAPI.noInvoiceFulfillments(soInfo.id);
        if (_.isEmpty(noInvoiceFulfillments)) {
            Message.warning("Please fulfill this SO first then create invoice");
            return;
        }
        if (noInvoiceFulfillments.length === 1) {
            navigate(`/workspace/sales/invoice/so/${soInfo.id}?fulfillmentId=${noInvoiceFulfillments[0].id}`);
            return;
        }

        getFulfillments();
        setVisible(true);
    }

    return (
        <>
            <Button type="primary" onClick={() => createInvoice()}>Create Invoice</Button>
            <Modal visible={visible} closeIcon={false}
                onCancel={() => setVisible(false)}
                onOk={() => {
                    const params = qs.stringify({ fulfillmentId: fulfillmentIds }, { arrayFormat: 'repeat' });
                    navigate(`/workspace/sales/invoice/so/${soInfo.id}?${params}`)
                }}
            >
                <Table rowKey="id" columns={[
                    { title: "Fulfillment Order#", dataIndex: "soFulfillmentNo" },
                    { title: "Date", dataIndex: "fulfillmentDate" }
                ]}
                    rowSelection={{
                        type: "checkbox",
                        onChange: (selectedRowKeys, selectedRows) => {
                            setFulfillmentIds(selectedRowKeys)
                        }
                    }}
                    data={data} />
            </Modal>
        </>
    )
}

export const SaleOrderInfo = () => {
    const params = useParams();
    const navigate = useNavigate();
    const soId = params.soId;
    const [info, setInfo] = useState({});
    const [fulfillmentList, setFulfillmentList] = useState([]);
    const [fulfillmentListTotal, setFulfillmentListTotal] = useState(0);
    const [fulfillmentListPage, setFulfillmentListPage] = useState(1);
    const [poList, setPOList] = useState([]);
    const [poListTotal, setPOListTotal] = useState(0);
    const [poListPage, setPOListPage] = useState(1);
    const [invoiceList, setInvoiceList] = useState([]);
    const [invoiceListTotal, setInvoiceListTotal] = useState(0);
    const [invoiceListPage, setInvoiceListPage] = useState(1);
    const [printLoading, setPrintLoading] = useState(false);

    const getInfo = useCallback(async () => {
        const values = await SOAPI.get(soId);
        const fulfillmentItems = await SOAPI.fulfillmentItems(soId);
        const fulfilledItemQuantities = _.chain(fulfillmentItems).groupBy("itemId").map((v, k) => {
            let totalQty = 0;
            _.forEach(v, (v) => {
                totalQty = Decimal.add(totalQty, v.qtyFulfilled).toNumber();
            });
            return {
                itemId: k,
                quantity: totalQty
            }
        }).value();
        _.forEach(values.items, (v) => {
            const fulfilledItem = _.find(fulfilledItemQuantities, { itemId: v.itemId.toString() });
            v.fulfilledQty = fulfilledItem != null ? fulfilledItem.quantity : 0;
        });

        setInfo(values);
    }, [soId]);

    const issue = async () => {
        await SOAPI.issue(info.id);
        Message.success("Sale order issued successfully");
        getInfo();
    }

    const unissue = async () => {
        await SOAPI.unissue(info.id);
        Message.success("Sale order unissued successfully");
        getInfo();
    }

    const shortClose = async () => {
        await SOAPI.shortClose(info.id);
        Message.success("Sale order short closed successfully");
        getInfo();
    }

    const voidSO = async () => {
        await SOAPI.voidSO(info.id);
        Message.success("Sale order voided successfully");
        getInfo();
    }

    const getFulfillments = useCallback(async () => {
        const values = await SOFulfillmentAPI.query(fulfillmentListPage, { soId: soId });
        setFulfillmentList(values.records);
        setFulfillmentListTotal(values.total);
    }, [fulfillmentListPage, soId])

    const getPOs = useCallback(async () => {
        const values = await SOPOAPI.query(poListPage, { soId: soId });
        setPOList(values.records);
        setPOListTotal(values.total);
    }, [poListPage, soId])

    const getInvoices = useCallback(async () => {
        const values = await InvoiceAPI.query(invoiceListPage, { soId: soId });
        setInvoiceList(values.records);
        setInvoiceListTotal(values.total);
    }, [invoiceListPage, soId])

    useEffect(() => {
        getInfo();
    }, [getInfo])

    useEffect(() => {
        getFulfillments();
    }, [getFulfillments]);

    useEffect(() => {
        getPOs();
    }, [getPOs]);

    useEffect(() => {
        getInvoices();
    }, [getInvoices]);

    return <Card title={
        <Space>
            <span>{info.soNo}</span>
            <Tag><EnumLabel enumObj={SOStatusDef} value={info.status} /></Tag>
        </Space>
    } extra={<Space>
        {![SOStatus.Voided, SOStatus.ShortClosed].includes(info.status) &&
            <Button type="primary" onClick={() => navigate(`/workspace/sales/so/${info.id}/edit`)}>Edit</Button>
        }
        {info.status === SOStatus.Created &&
            <Button type="primary" onClick={() => issue()}>Issue</Button>
        }
        {info.status === SOStatus.Issued &&
            <Button type="primary" onClick={() => unissue()}>Unissue</Button>
        }
        {[SOStatus.Created, SOStatus.Issued, SOStatus.Picked, SOStatus.Partial, SOStatus.PartialFulfilledInvoiced].includes(info.status) &&
            <Button type="primary" onClick={() => navigate(`/workspace/sales/so-pick-order/so/${info.id}`)}>Pick</Button>
        }
        {[SOStatus.Created, SOStatus.Issued, SOStatus.Picked, SOStatus.Partial, SOStatus.PartialFulfilledInvoiced].includes(info.status) &&
            <Button type="primary" onClick={() => navigate(`/workspace/sales/so-fulfillment/so/${info.id}`)}>Fulfill SO</Button>
        }
        {(info.status === SOStatus.Partial || info.status === SOStatus.PartialFulfilledInvoiced) &&
            <Button type="primary" onClick={() => shortClose()}>Short Close</Button>
        }
        {info.status !== SOStatus.Voided &&
            <CreateInvoiceButton soInfo={info} />
        }
        {[SOStatus.Created, SOStatus.Issued].includes(info.status) &&
            <Popconfirm title="Are you sure you want to void sale order?"
                onOk={() => voidSO()}>
                <Button type="primary" status="danger">Void</Button>
            </Popconfirm>
        }
        {info.status !== SOStatus.Voided &&
            <Button type="primary" onClick={async () => {
                setPrintLoading(true);
                try {
                    const res = await SOAPI.print(info.id);
                    FileUtil.download(res.data, `${info.soNo}.pdf`);
                } catch (error) {
                    Message.error(error.message);
                } finally {
                    setPrintLoading(false);
                }
            }} loading={printLoading}>Print</Button>
        }
        {info.status !== SOStatus.Voided &&
            <SendEmailButton getEmailContent={async () => {
                return await SOAPI.getEmail(info.id);
            }} getToList={async () => {
                const customer = await CustomerAPI.get(info.customerId);
                return customer.contacts;
            }} sendEmail={async (val) => {
                await SOAPI.sendEmail(info.id, val);
            }} />
        }
        <Button onClick={() => navigate("/workspace/sales/so")}>Back</Button>
    </Space>
    }>
        <Descriptions colon=":"
            data={[{
                label: "Customer",
                value: info.customerName
            }, {
                label: "Ship to",
                value: <Address value={{
                    name: info.shipToContactName,
                    address: info.shipToAddress,
                    city: info.shipToCity,
                    state: info.shipToState,
                    zip: info.shipToZip
                }} inline />
            }, {
                label: "SO Date",
                value: info.soDate
            }, {
                label: "Ready",
                value: info.readyDate
            }, {
                label: "Due Date",
                value: info.dueDate
            }, {
                label: "Term",
                value: <EnumLabel enumObj={TermsDef} value={info.term} />
            }, {
                label: "Reference#",
                value: <Reference value={info.reference} />,
                span: 2
            }, {
                label: "SO Total",
                value: <Currency value={info.totalAmount} />
            }, {
                label: "Memo",
                value: info.memo,
                span: 3
            }]} />

        <Table rowKey="id" columns={[
            { title: "Item Number", dataIndex: "itemNo" },
            { title: "Item Name", dataIndex: "itemName" },
            { title: "Description", dataIndex: "description" },
            {
                title: "Qty",
                render: (col, record) => <Number value={record.quantity} />
            },
            {
                title: "Alternative Qty",
                render: (col, record) => <span>
                    <Number value={record.alternativeQty} /> {record.additionalUom}
                </span>
            },
            {
                title: "Fulfilled Qty",
                render: (col, record) => <Number value={record.fulfilledQty} />
            },
            {
                title: "Open Qty",
                render: (col, record) => <Number value={Decimal.sub(record.quantity, record.fulfilledQty).toNumber()} />
            },
            { title: "U/M", dataIndex: "uom" },
            { title: "Rate", dataIndex: "rate" },
            { title: "Amount", render: (col, record) => <Currency value={record.amount} /> },
        ]}
            data={info.items}
        />

        <Table rowKey="id" style={{ marginTop: 15 }} columns={[
            {
                title: "SO Fulfillment#",
                render: (col, record) =>
                    <Link to={"/workspace/sales/so-fulfillment/" + record.id}>{record.soFulfillmentNo}</Link>
            },
            { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={FulfillmentStatusDef} value={record.status} /> },
            { title: "Fulfillment Date", dataIndex: "fulfillmentDate" },
        ]} data={fulfillmentList} pagination={{ total: fulfillmentListTotal, current: fulfillmentListPage, pageSize: 20 }} onChange={(pagination) => setFulfillmentListPage(pagination.current)} />

        <Table rowKey="id" style={{ marginTop: 15 }} columns={[
            {
                title: "Pick Order#",
                render: (col, record) => {
                    return <Link to={"/workspace/sales/so-pick-order/" + record.id}>{record.pickOrderNo}</Link>
                }
            },
            { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={SOPOStatusDef} value={record.status} /> },
            { title: "Warehouse", dataIndex: "warehouseName" },
        ]} data={poList} pagination={{ total: poListTotal, current: poListPage, pageSize: 20 }} onChange={(pagination) => setPOListPage(pagination.current)} />

        <Table rowKey="id" style={{ marginTop: 15 }} columns={[
            {
                title: "Invoice#",
                render: (col, record) => {
                    return <Link to={"/workspace/sales/invoice/" + record.id}>{record.invoiceNo}</Link>
                }
            },
            { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={InvoiceStatusDef} value={record.status} /> },
            { title: "Invoice Date", dataIndex: "invoiceDate" },
        ]} data={invoiceList} pagination={{ total: invoiceListTotal, current: invoiceListPage, pageSize: 20 }} onChange={(pagination) => setInvoiceListPage(pagination.current)} />
    </Card>
}
